import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import ApiClient from "#/redux/api/ApiClient.js";
import { handleLoadingAndErrorState } from "#/redux/util/reduxUtils.js";
import { getErrorMessage } from "#/utils/APIUtils";

const baseUrl = "/api/priponke";

export const deleteAttachment = createAsyncThunk(
  "deleteAttachment",
  async (file, { dispatch }) => {
    if (file?.pripetaId) {
      await dispatch(
        detachAttachmentFromEntity({
          pripetaPriponkaId: file.pripetaId,
          version: file.version,
        })
      ).unwrap();
    }

    if (file?.id) {
      await dispatch(
        deleteAttachmentFile({
          attachmentId: file.id,
          version: file.version,
        })
      ).unwrap();
    }
  }
);

export const uploadAttachment = async (file, dtoVo = {}) => {
  const formData = new FormData();

  const dto = {
    filename: file.name,
    ...dtoVo,
  };
  formData.append("file", file.content);
  formData.append(
    "dto",
    new Blob([JSON.stringify(dto)], { type: "application/json" })
  );
  const response = await ApiClient.postForFile(
    `${baseUrl}/priponka/uploadFile`,
    formData
  );
  const text = await response.data.text();
  const responseObject = JSON.parse(text).output;
  return {
    ...responseObject,
    id: responseObject.id,
    temporaryId: responseObject.id,
    version: responseObject.version,
  };
};

export const deleteAttachmentFile = createAsyncThunk(
  "attachment/deleteFile",
  async ({ attachmentId, version }, { rejectWithValue }) => {
    try {
      await ApiClient.delete(`${baseUrl}/priponka/${attachmentId}`, {
        version,
      });
      return attachmentId;
    } catch (e) {
      return rejectWithValue(getErrorMessage(e));
    }
  }
);
export const detachAttachmentFromEntity = createAsyncThunk(
  "attachment/detach",
  async ({ pripetaPriponkaId, version }, { rejectWithValue }) => {
    try {
      await ApiClient.delete(
        `${baseUrl}/pripetaPriponka/${pripetaPriponkaId}?version=${version}`
      );
      return pripetaPriponkaId;
    } catch (e) {
      return rejectWithValue(getErrorMessage(e));
    }
  }
);

export const uploadAttachmentThunk = createAsyncThunk(
  "attachment/upload",
  async ({ file, dto = {} }, { rejectWithValue }) => {
    try {
      return await uploadAttachment(file, dto);
    } catch (error) {
      console.error("File upload error", error);
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const addAttachmentToEntity = createAsyncThunk(
  "attachment/addToEntity",
  async ({ entityId, attachmentId, entityType, note }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.post(`${baseUrl}/pripetaPriponka`, {
        priponkaId: attachmentId,
        entitetaId: entityId,
        tipEntitete: entityType,
        opomba: note,
      });
      return response.data;
    } catch (error) {
      console.error("File attach error", error);
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const addAttachmentsToEntitiesMultiple = createAsyncThunk(
  "attachment/addToEntitiesMultiple",
  async (dtos, { rejectWithValue }) => {
    try {
      await ApiClient.post(`${baseUrl}/pripetaPriponka/multiple`, dtos);
      return dtos;
    } catch (error) {
      console.error("File attach multiple error", error);
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const downloadAttachment = createAsyncThunk(
  "attachment/download",
  async (attachmentId, { rejectWithValue }) => {
    try {
      const fileMetadataResponse = await ApiClient.get(
        `${baseUrl}/priponka/${attachmentId}`
      );

      const { filename, mimeType } = fileMetadataResponse.data;
      const response = await ApiClient.getFile(
        `${baseUrl}/priponka/downloadFile/${attachmentId}`,
        null,
        filename
      );

      const blob = new Blob([response.data], {
        type: mimeType,
      });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      URL.revokeObjectURL(url);
      a.remove();
      return null;
    } catch (error) {
      console.error("Get attachment error", error);
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getAttachmentBlobById = createAsyncThunk(
  "attachment/blobById",
  async (attachmentId, { rejectWithValue }) => {
    try {
      const fileMetadataResponse = await ApiClient.get(
        `${baseUrl}/priponka/${attachmentId}`
      );

      const { filename, mimeType } = fileMetadataResponse.data;
      const response = await ApiClient.getFile(
        `${baseUrl}/priponka/downloadFile/${attachmentId}`
      );

      return {
        attachmentId,
        filename,
        mimeType,
        blobText: await response.data.text(),
      };
    } catch (error) {
      console.error("Get attachment blob error", error);
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getAttachmentPage = createAsyncThunk(
  "attachment/page",
  async ({ page, filter, sort }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        `${baseUrl}/priponka/page`,
        undefined,
        page,
        filter,
        sort,
        false
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getEventAttachmentPage = createAsyncThunk(
  "attachment/event/page",
  async ({ page, filter, sort }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        `${baseUrl}/pripetaPriponka/page`,
        undefined,
        page,
        filter,
        sort,
        false
      );

      return response.data;
    } catch (error) {
      console.error("Get attachment error", error);
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getAttachmentById = createAsyncThunk(
  "attachment/byId",
  async (id, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(`${baseUrl}/pripetaPriponka/${id}`);

      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const thunks = [
  uploadAttachmentThunk,
  addAttachmentToEntity,
  addAttachmentsToEntitiesMultiple,
  downloadAttachment,
  getAttachmentBlobById,
  getAttachmentPage,
  getEventAttachmentPage,
];

const attachmentSlice = createSlice({
  name: "attachment",
  initialState: {
    isLoading: false,
    error: null,
    attachmentPage: {}, // Standalone attachments
    entityAttachmentPage: {}, // Entries connecting actual attachment to an entity/event
    byEntityId: {},
    byId: {},
  },
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getAttachmentById.fulfilled, (state, action) => {
      state.isLoading = false;
      state.byId[action.payload.id] = action.payload;
    });
    builder.addCase(getAttachmentById.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });
    builder.addCase(getAttachmentById.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(getAttachmentPage.fulfilled, (state, action) => {
      state.attachmentPage = action.payload;
    });
    builder.addCase(getEventAttachmentPage.fulfilled, (state, action) => {
      state.entityAttachmentPage = action.payload;
      action.payload.content.forEach((entityAttachment) => {
        if (state.byEntityId[entityAttachment.entitetaId] == null) {
          state.byEntityId[entityAttachment.entitetaId] = [];
        }

        const priponka = entityAttachment?.priponka;

        const newAttachment = {
          id: priponka.id,
          temporaryId: priponka.id,
          filename: priponka.filename,
          name: priponka.filename,
          type: priponka.mimeType,
          mimeType: priponka.mimeType,
          sizeBytes: priponka.sizeBytes,
          size: priponka.sizeBytes,

          pripetaId: entityAttachment.id,
          version: entityAttachment.version,
          pripetaVersion: entityAttachment.version,
          createdOn: entityAttachment.createdOn,
          createdBy: entityAttachment.createdBy,
          tipEntitete: entityAttachment.tipEntitete,
          entitetaId: entityAttachment.entitetaId,
          opomba: entityAttachment.opomba,
        };
        const idx = state.byEntityId[entityAttachment.entitetaId].findIndex(
          (a) => a.pripetaId === newAttachment.pripetaId
        );

        if (idx === -1) {
          state.byEntityId[entityAttachment.entitetaId].push(newAttachment);
        } else {
          state.byEntityId[entityAttachment.entitetaId][idx] = newAttachment;
        }
      });
    });
    builder.addCase(detachAttachmentFromEntity.fulfilled, (state, action) => {
      const removedPripetaId = action.payload;

      Object.keys(state.byEntityId).forEach((entityId) => {
        state.byEntityId[entityId] = state.byEntityId[entityId].filter(
          (a) => a.pripetaId !== removedPripetaId
        );

        if (state.byEntityId[entityId].length === 0) {
          delete state.byEntityId[entityId];
        }
      });
    });

    builder.addCase(deleteAttachmentFile.fulfilled, (state, action) => {
      const removedAttachmentId = action.payload;

      Object.keys(state.byEntityId).forEach((entityId) => {
        state.byEntityId[entityId] = state.byEntityId[entityId].filter(
          (a) => a.id !== removedAttachmentId
        );

        if (state.byEntityId[entityId].length === 0) {
          delete state.byEntityId[entityId];
        }
      });

      // backward compatibility
      if (state.attachmentPage?.content) {
        state.attachmentPage.content = state.attachmentPage.content.filter(
          (p) => p.id !== removedAttachmentId
        );
      }
    });

    handleLoadingAndErrorState(builder, thunks);
  },
});

export default attachmentSlice.reducer;
